import type { StrategicChokepoint, CarrierGroup } from '@/types';

// ---------------------------------------------------------------------------
// Strategic Chokepoints — Critical Maritime / Land Passages
// ---------------------------------------------------------------------------

export const STRATEGIC_CHOKEPOINTS: StrategicChokepoint[] = [
  {
    id: 'choke-malacca',
    name: 'Strait of Malacca',
    nameKo: '말라카 해협',
    lon: 103.5, lat: 1.5,
    type: 'strait',
    dailyShips: 1000,
    oilMbdEquivalent: 16,
    threatLevel: 'high',
    controlledBy: ['Singapore', 'Malaysia', 'Indonesia'],
    strategicNotes: '40% of global trade; 25% of world oil. Chinese blockade scenario. Piracy risk.',
  },
  {
    id: 'choke-hormuz',
    name: 'Strait of Hormuz',
    nameKo: '호르무즈 해협',
    lon: 56.5, lat: 26.5,
    type: 'strait',
    dailyShips: 21,
    oilMbdEquivalent: 21,
    threatLevel: 'critical',
    controlledBy: ['Iran (northern shore)', 'UAE / Oman (southern)'],
    strategicNotes: '21% of global petroleum. Iran can mine/close. Tanker attacks 2019-2024. US 5th Fleet patrols.',
  },
  {
    id: 'choke-suez',
    name: 'Suez Canal',
    nameKo: '수에즈 운하',
    lon: 32.5, lat: 30.5,
    type: 'canal',
    dailyShips: 51,
    oilMbdEquivalent: 9,
    threatLevel: 'high',
    controlledBy: ['Egypt (Suez Canal Authority)'],
    strategicNotes: 'Europe-Asia shortcut. Ever Given incident 2021. Houthi Red Sea attacks rerouting ships via Cape of Good Hope.',
  },
  {
    id: 'choke-babelm',
    name: 'Bab-el-Mandeb',
    nameKo: '밥엘만뎁 해협',
    lon: 43.3, lat: 12.5,
    type: 'strait',
    dailyShips: 21,
    oilMbdEquivalent: 9,
    threatLevel: 'critical',
    controlledBy: ['Djibouti (US/Chinese base)', 'Yemen (Houthi)', 'Eritrea'],
    strategicNotes: 'Houthi Red Sea attack campaign ongoing. 25% of container traffic diverted. US/UK strikes on Houthi sites.',
  },
  {
    id: 'choke-taiwan',
    name: 'Taiwan Strait',
    nameKo: '대만 해협',
    lon: 120.0, lat: 24.5,
    type: 'passage',
    dailyShips: 300,
    oilMbdEquivalent: 0,
    threatLevel: 'critical',
    controlledBy: ['Taiwan (ROC Navy)', 'China (PLA Navy)', 'US 7th Fleet'],
    strategicNotes: 'Highest potential flashpoint. PLA exercises simulate blockade. TSMC chips critical supply chain. US freedom of navigation operations.',
  },
  {
    id: 'choke-scs',
    name: 'South China Sea',
    nameKo: '남중국해',
    lon: 114.0, lat: 10.0,
    type: 'passage',
    dailyShips: 1200,
    oilMbdEquivalent: 5,
    threatLevel: 'high',
    controlledBy: ['China (de facto islands)', 'Philippines/Vietnam/Malaysia (contest)'],
    strategicNotes: 'China claims 90% via 9-dash line (illegal per UNCLOS). Ayungin Shoal standoff ongoing.',
  },
  {
    id: 'choke-bosphorus',
    name: 'Bosphorus / Dardanelles',
    nameKo: '보스포루스 해협',
    lon: 29.0, lat: 41.0,
    type: 'strait',
    dailyShips: 140,
    oilMbdEquivalent: 3,
    threatLevel: 'high',
    controlledBy: ['Turkey (Montreux Convention)'],
    strategicNotes: 'Russia Black Sea Fleet egress. Turkey closed to warships under Montreux during Ukraine war. Russian grain/oil exports.',
  },
  {
    id: 'choke-panama',
    name: 'Panama Canal',
    nameKo: '파나마 운하',
    lon: -79.5, lat: 9.1,
    type: 'canal',
    dailyShips: 36,
    oilMbdEquivalent: 1.5,
    threatLevel: 'medium',
    controlledBy: ['Panama'],
    strategicNotes: 'US-China rivalry. Trump demanded return of control. Drought reduced capacity 2024. China port infrastructure nearby.',
  },
  {
    id: 'choke-luzon',
    name: 'Luzon Strait',
    nameKo: '루손 해협',
    lon: 121.0, lat: 20.5,
    type: 'passage',
    dailyShips: 90,
    oilMbdEquivalent: 0,
    threatLevel: 'high',
    controlledBy: ['Philippines', 'Taiwan', 'China (PLA)'],
    strategicNotes: 'PLA submarine route from Pacific to Indian Ocean. Key Taiwan contingency access route.',
  },
  {
    id: 'choke-demark',
    name: 'Danish Straits',
    nameKo: '덴마크 해협',
    lon: 12.5, lat: 56.5,
    type: 'passage',
    dailyShips: 60,
    oilMbdEquivalent: 1,
    threatLevel: 'medium',
    controlledBy: ['Denmark', 'Sweden', 'Germany'],
    strategicNotes: 'Russia Baltic Fleet egress. NATO now controls with Sweden+Finland membership. Strategic chokepoint for Russian naval power.',
  },
  {
    id: 'choke-sunda',
    name: 'Sunda Strait',
    nameKo: '순다 해협',
    lon: 105.9, lat: -6.0,
    type: 'strait',
    dailyShips: 200,
    oilMbdEquivalent: 2,
    threatLevel: 'medium',
    controlledBy: ['Indonesia'],
    strategicNotes: 'Malacca alternative. Krakatau volcanic risk. US 7th Fleet transit. PLA alternate route.',
  },
];

// ---------------------------------------------------------------------------
// Carrier Battle Groups — OSINT-based deployments (March 2026)
// ---------------------------------------------------------------------------

export const CARRIER_GROUPS: CarrierGroup[] = [
  // US Navy — 11 carriers, typically 4-6 deployed
  {
    id: 'csg-12', carrierName: 'USS Gerald R. Ford (CVN-78)',
    country: 'United States', countryCode: 'US',
    lat: 36.5, lon: -8.5, heading: 45,
    groupName: 'CSG-12 (Ford Strike Group)',
    airWing: 'CVW-8 (70 aircraft)',
    destroyers: ['USS Ramage (DDG-61)', 'USS McFaul (DDG-74)', 'USS Forrest Sherman (DDG-98)'],
    cruisers: ['USS Normandy (CG-60)'],
    submarines: ['USS Minnesota (SSN-783)'],
    supplyShips: ['USNS San Jose (T-AFS-7)'],
    mission: 'NATO Atlantic deterrence / Russia containment',
    region: 'Eastern Atlantic',
    threatLevel: 'medium',
  },
  {
    id: 'csg-5', carrierName: 'USS Ronald Reagan (CVN-76)',
    country: 'United States', countryCode: 'US',
    lat: 14.5, lon: 144.8, heading: 270,
    groupName: 'CSG-5 (Reagan Strike Group — Japan Forward Deployed)',
    airWing: 'CVW-5 (70 aircraft, Atsugi-based)',
    destroyers: ['USS Mustin (DDG-89)', 'USS Rafael Peralta (DDG-115)', 'USS Howard (DDG-83)'],
    cruisers: ['USS Antietam (CG-54)'],
    submarines: ['USS Ohio (SSGN-726)'],
    supplyShips: ['USNS Pecos (T-AO-197)'],
    mission: 'Taiwan Strait deterrence / DPRK response / Indo-Pacific presence',
    region: 'Western Pacific / Guam',
    threatLevel: 'critical',
  },
  {
    id: 'csg-2', carrierName: 'USS Harry S. Truman (CVN-75)',
    country: 'United States', countryCode: 'US',
    lat: 37.5, lon: 23.0, heading: 90,
    groupName: 'CSG-2 (Truman Strike Group)',
    airWing: 'CVW-1 (70 aircraft)',
    destroyers: ['USS Cole (DDG-67)', 'USS Gravely (DDG-107)', 'USS Porter (DDG-78)'],
    cruisers: ['USS San Jacinto (CG-56)'],
    submarines: ['USS Virginia (SSN-774)'],
    supplyShips: ['USNS Arctic (T-AOE-8)'],
    mission: 'Eastern Mediterranean / Red Sea Houthi ops / Israel deterrence',
    region: 'Eastern Mediterranean',
    threatLevel: 'critical',
  },
  {
    id: 'csg-1', carrierName: 'USS Carl Vinson (CVN-70)',
    country: 'United States', countryCode: 'US',
    lat: 22.0, lon: 118.0, heading: 90,
    groupName: 'CSG-1 (Vinson Strike Group)',
    airWing: 'CVW-2 (70 aircraft)',
    destroyers: ['USS Sterett (DDG-104)', 'USS Dewey (DDG-105)', 'USS Chung-Hoon (DDG-93)'],
    cruisers: ['USS Lake Champlain (CG-57)'],
    submarines: ['USS Tucson (SSN-770)'],
    supplyShips: ['USNS Bridge (T-AOE-10)'],
    mission: 'South China Sea FONOP / Taiwan support / DPRK monitoring',
    region: 'South China Sea',
    threatLevel: 'critical',
  },
  // China PLAN
  {
    id: 'plan-003', carrierName: 'CNS Fujian (Type 003)',
    country: 'China', countryCode: 'CN',
    lat: 30.0, lon: 122.5, heading: 90,
    groupName: 'PLAN Type 003 Strike Group',
    airWing: 'J-35 + J-15B (carrier-based)',
    destroyers: ['Nanchang (Type 055)', 'Wuxi (Type 052D)', 'Qujing (Type 052D)'],
    cruisers: [],
    submarines: ['Type 093B SSN'],
    supplyShips: ['Hulunhu (Type 903A)'],
    mission: 'Taiwan reunification exercises / Pacific projection / anti-access drills',
    region: 'East China Sea / Pacific',
    threatLevel: 'critical',
  },
  {
    id: 'plan-002', carrierName: 'CNS Shandong (Type 002)',
    country: 'China', countryCode: 'CN',
    lat: 20.0, lon: 118.0, heading: 135,
    groupName: 'PLAN Type 002 Strike Group',
    airWing: 'J-15 carrier aircraft',
    destroyers: ['Type 052D destroyer x3'],
    cruisers: ['Type 055 destroyer (cruiser equivalent)'],
    submarines: ['Type 094A SSBN'],
    supplyShips: ['Type 901 fast combat supply ship'],
    mission: 'South China Sea dominance / Taiwan blockade simulation',
    region: 'South China Sea',
    threatLevel: 'critical',
  },
  // UK Royal Navy
  {
    id: 'hms-qe', carrierName: 'HMS Queen Elizabeth (R08)',
    country: 'United Kingdom', countryCode: 'GB',
    lat: 50.5, lon: -4.0, heading: 200,
    groupName: 'UK Carrier Strike Group 21',
    airWing: 'F-35B Lightnings (617/809 Sqn) + Wildcat/Merlin',
    destroyers: ['HMS Dragon (D35)', 'HMS Dauntless (D33)'],
    cruisers: [],
    submarines: ['HMS Artful (S121)'],
    supplyShips: ['RFA Fort Victoria'],
    mission: 'NATO Baltic deterrence / AUKUS Indo-Pacific deployment prep',
    region: 'North Atlantic / GIUK Gap',
    threatLevel: 'medium',
  },
  // France Marine Nationale
  {
    id: 'fs-cdg', carrierName: 'FS Charles de Gaulle (R91)',
    country: 'France', countryCode: 'FR',
    lat: 43.3, lon: 5.0, heading: 270,
    groupName: 'Groupe Aéronaval (GAN)',
    airWing: 'Rafale Marine F3-R + E-2C Hawkeye',
    destroyers: ['FS Chevalier Paul (D621)', 'FS Provence (D652)'],
    cruisers: [],
    submarines: ['SNA Casabianca (S603)'],
    supplyShips: ['BSAM Seine'],
    mission: 'Mediterranean power projection / NATO southern flank',
    region: 'Western Mediterranean',
    threatLevel: 'medium',
  },
  // India
  {
    id: 'ins-vikrant', carrierName: 'INS Vikrant (R11)',
    country: 'India', countryCode: 'IN',
    lat: 15.5, lon: 74.0, heading: 180,
    groupName: 'Indian Carrier Battle Group',
    airWing: 'MiG-29K Fulcrums + MH-60R',
    destroyers: ['INS Visakhapatnam (D66)', 'INS Mormugao (D67)'],
    cruisers: [],
    submarines: ['INS Khanderi (S22)'],
    supplyShips: ['INS Deepak (A50)'],
    mission: 'Indian Ocean control / IOR deterrence / Pakistan contingency',
    region: 'Arabian Sea / Indian Ocean',
    threatLevel: 'medium',
  },
];

export function fetchCarrierGroups(): CarrierGroup[] {
  const now = Date.now();
  // Add subtle drift movement
  return CARRIER_GROUPS.map((cg, i) => ({
    ...cg,
    lat: cg.lat + Math.sin(now / 7200000 + i * 1.3) * 0.2,
    lon: cg.lon + Math.cos(now / 7200000 + i * 1.3) * 0.2,
  }));
}

export function fetchChokepoints(): StrategicChokepoint[] {
  return STRATEGIC_CHOKEPOINTS;
}
